import type { ReviewWithReplyDto } from '@up2stay/up2promo-api-client';
import { Avatar, AvatarFallback, cn } from '@up2stay/up2promo-ui';

import { formatRelativeTime } from '@/core/components/leads/lead-detail/format-relative-time';
import { StarRating } from '@/core/components/reputation/reputation-bits';
import {
  CHART,
  type SentimentPolarity,
} from '@/core/components/reputation/reputation-chart-config';
import {
  colorForReviewAuthor,
  getReviewAuthorInitials,
} from '@/core/components/reputation/reviews/review-avatar';

export function ReviewAuthorAvatar({
  name,
  className,
}: {
  name: string;
  className?: string;
}) {
  return (
    <Avatar className={cn('shrink-0', className)}>
      <AvatarFallback
        className={cn('font-semibold text-white', colorForReviewAuthor(name))}
      >
        {getReviewAuthorInitials(name)}
      </AvatarFallback>
    </Avatar>
  );
}

export function ReviewListItem({
  review,
  locale,
  sentimentLabels,
  noTextLabel,
  active,
  urgent,
  onSelect,
}: {
  review: ReviewWithReplyDto;
  locale: string;
  sentimentLabels: Record<SentimentPolarity, string>;
  noTextLabel: string;
  active: boolean;
  urgent: boolean;
  onSelect: () => void;
}) {
  const polarity = review.sentiment?.polarity ?? null;
  return (
    <button
      type="button"
      data-review-id={review.id}
      aria-current={active ? 'true' : undefined}
      onClick={onSelect}
      className={cn(
        'flex min-h-[72px] w-full items-start gap-3 border-b border-l-[3px] px-4 py-3.5 text-left transition-colors last:border-b-0',
        active
          ? 'border-l-primary bg-accent'
          : 'border-l-transparent hover:bg-muted/40',
        urgent && !active && 'bg-destructive/[0.04]',
      )}
    >
      <ReviewAuthorAvatar
        name={review.authorName}
        className="size-[34px] text-xs"
      />
      <span className="min-w-0 flex-1">
        <span className="flex items-center gap-1.5">
          <span className="truncate text-[13.5px] font-semibold">
            {review.authorName}
          </span>
          <span className="text-muted-foreground ml-auto shrink-0 text-[11px]">
            {formatRelativeTime(review.reviewedAt, locale)}
          </span>
        </span>
        <span className="mt-0.5 flex items-center gap-2">
          <StarRating rating={review.rating} />
          {polarity ? (
            <span className="text-muted-foreground flex items-center gap-1.5 text-[11px]">
              <span
                aria-hidden
                className="size-1.5 rounded-full"
                style={{ background: CHART[polarity] }}
              />
              {sentimentLabels[polarity]}
            </span>
          ) : null}
        </span>
        {review.text ? (
          <span className="text-muted-foreground mt-1 block truncate text-[12.5px]">
            {review.text}
          </span>
        ) : (
          <span className="text-muted-foreground/70 mt-1 block text-[12.5px] italic">
            {noTextLabel}
          </span>
        )}
      </span>
    </button>
  );
}
